/**
 * Patient View Tabs Storage Service
 *
 * Provides staffId-based isolation for patient view tabs using IndexedDB
 * to prevent cross-user data leakage while maintaining persistence
 * across browser sessions. Maintains synchronous interface for backwards compatibility.
 */

const DB_NAME = "MPSPatientViewTabs";
const DB_VERSION = 1;
const STORE_NAME = "patientViewTabs";

class PatientViewTabsStorageService {
  constructor() {
    this.db = null;
    this.dbReady = false;
    this.tabsCache = new Map(); // staffId -> tabs cache
    this.dbPromise = this.initDB();
    
    // Initialize database in background
    this.dbPromise.then(() => {
      this.dbReady = true;
      this.migrateFromLocalStorage();
    }).catch(error => {
      console.error("Failed to initialize PatientViewTabs IndexedDB:", error);
    });
  }

  /**
   * Initialize IndexedDB database
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("Failed to open IndexedDB for patient view tabs");
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object store with staffId as key
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: "staffId",
          });
          store.createIndex("staffId", "staffId", { unique: true });
        }
      };
    });
  }

  /**
   * Get current staff ID from Redux store
   */
  getCurrentStaffId() {
    try {
      // Import store dynamically to avoid circular dependencies
      const { store } = require("../State/store/configureStore");
      const state = store.getState();
      const staffId =
        state?.user?.data?.id ||
        state?.user?.data?.staffId ||
        state?.user?.data?.userId ||
        null;

      return staffId;
    } catch (error) {
      console.warn("Could not get current staff ID:", error);
      return null;
    }
  }

  /**
   * Get tab order (array of tab IDs) for current staff
   * Returns cached data immediately, updates from IndexedDB in background
   */
  getTabOrderForCurrentStaff() {
    const staffId = this.getCurrentStaffId();
    
    console.log(`🟩 GETTING tab order for staff ${staffId}`);
    
    if (!staffId) {
      console.warn("❌ No staff ID, falling back to localStorage");
      return this.fallbackGetOrderFromLocalStorage();
    }

    // Return cached data if available
    if (this.tabsCache.has(staffId)) {
      const cachedOrder = this.tabsCache.get(staffId);
      console.log(`✅ Found cached order for staff ${staffId}:`, cachedOrder);
      return cachedOrder;
    }

    console.log(`⚠️ No cached order for staff ${staffId}`);

    // If IndexedDB is ready, try to load synchronously first
    if (this.dbReady && this.db) {
      console.log(`🔄 IndexedDB ready, trying synchronous load for staff ${staffId}...`);
      
      try {
        // Try synchronous read from IndexedDB
        const transaction = this.db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(staffId);
        
        // Set up the success handler
        request.onsuccess = (event) => {
          const result = event.target.result;
          if (result && result.tabOrder && result.tabOrder.length > 0) {
            console.log(`✅ Found IndexedDB order for staff ${staffId}:`, result.tabOrder);
            this.tabsCache.set(staffId, result.tabOrder);
            // Force a re-render by dispatching a custom event
            window.dispatchEvent(new CustomEvent('tabOrderLoaded'));
          }
        };
      } catch (error) {
        console.log(`⚠️ Synchronous IndexedDB read failed:`, error);
      }
      
      // Also load asynchronously in background
      this.loadTabOrderFromIndexedDB(staffId);
    } else {
      console.log(`⚠️ IndexedDB not ready yet for staff ${staffId}`);
    }

    // Return from localStorage as fallback for immediate response
    const fallbackOrder = this.fallbackGetOrderFromLocalStorage();
    console.log(`🔄 Using fallback order for staff ${staffId}:`, fallbackOrder);
    this.tabsCache.set(staffId, fallbackOrder);
    return fallbackOrder;
  }

  /**
   * Store tab order (array of tab IDs) for current staff
   * Updates cache immediately, saves to IndexedDB in background
   */
  storeTabOrderForCurrentStaff(tabs) {
    const staffId = this.getCurrentStaffId();
    
    // Extract just the IDs from tab objects
    const tabOrder = tabs.map(tab => tab.id);
    
    console.log(`🟦 STORING tab order for staff ${staffId}:`, tabOrder);
    
    if (!staffId) {
      console.warn("❌ No staff ID, falling back to localStorage");
      this.fallbackStoreOrderToLocalStorage(tabOrder);
      return;
    }

    // Update cache immediately for synchronous access
    this.tabsCache.set(staffId, tabOrder);
    console.log(`✅ Updated cache for staff ${staffId}`);

    // Store to IndexedDB in background
    if (this.dbReady && this.db) {
      console.log(`🔄 Saving to IndexedDB for staff ${staffId}...`);
      this.saveTabOrderToIndexedDB(staffId, tabOrder);
    } else {
      console.warn("⚠️ Database not ready, storing to localStorage as fallback");
      this.fallbackStoreOrderToLocalStorage(tabOrder);
    }
  }

  /**
   * Load tab order from IndexedDB asynchronously
   */
  async loadTabOrderFromIndexedDB(staffId) {
    try {
      if (!this.db) await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(staffId);

        request.onsuccess = () => {
          const result = request.result;
          const tabOrder = result ? result.tabOrder || [] : [];
          
          console.log(`✅ Loaded from IndexedDB for staff ${staffId}:`, tabOrder);
          
          // Update cache with loaded data
          this.tabsCache.set(staffId, tabOrder);
          resolve(tabOrder);
        };

        request.onerror = () => {
          console.error("Failed to load tab order for staff:", staffId);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error("Error loading tab order from IndexedDB:", error);
      return [];
    }
  }

  /**
   * Save tab order to IndexedDB asynchronously
   */
  async saveTabOrderToIndexedDB(staffId, tabOrder) {
    try {
      if (!this.db) await this.dbPromise;

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      const data = {
        staffId: staffId,
        tabOrder: tabOrder,
        lastUpdated: new Date().toISOString(),
      };

      const request = store.put(data);
      
      request.onsuccess = () => {
        console.log(`✅ Successfully stored tab order in IndexedDB for staff ${staffId}:`, tabOrder);
      };
      
      request.onerror = () => {
        console.error(`Failed to store tab order for staff ${staffId}`);
        // Fallback to localStorage if IndexedDB fails
        this.fallbackStoreOrderToLocalStorage(tabOrder);
      };
    } catch (error) {
      console.error("Error saving tab order to IndexedDB:", error);
      // Fallback to localStorage if IndexedDB fails
      this.fallbackStoreOrderToLocalStorage(tabOrder);
    }
  }

  /**
   * Migrate existing localStorage data to IndexedDB for current staff
   */
  async migrateFromLocalStorage() {
    try {
      const staffId = this.getCurrentStaffId();
      if (!staffId) return;

      const StorageService = require("./storageService").default;
      const PATIENT_VIEW_TABS = "PATIENT_VIEW_TABS";
      
      const existingTabs = StorageService.getFromLocalStorage(PATIENT_VIEW_TABS);
      
      if (existingTabs && existingTabs.length > 0) {
        // Check if we already have data for this staff in IndexedDB
        const existingIndexedDBOrder = await this.loadTabOrderFromIndexedDB(staffId);
        
        if (!existingIndexedDBOrder || existingIndexedDBOrder.length === 0) {
          // Extract tab order from existing tabs
          const tabOrder = existingTabs.map(tab => tab.id);
          
          // Migrate the tab order to IndexedDB for this staff
          await this.saveTabOrderToIndexedDB(staffId, tabOrder);
          
          // Update cache
          this.tabsCache.set(staffId, tabOrder);
          
          // Clear localStorage to prevent future confusion
          StorageService.removeFromLocalStorage(PATIENT_VIEW_TABS);
          
          console.log(
            `Migrated ${tabOrder.length} tab order from localStorage to IndexedDB for staff ${staffId}:`,
            tabOrder
          );
        }
      }
    } catch (error) {
      console.error("Error migrating patient view tabs from localStorage:", error);
    }
  }

  /**
   * Fallback to localStorage for backwards compatibility - get order
   */
  fallbackGetOrderFromLocalStorage() {
    try {
      const StorageService = require("./storageService").default;
      const PATIENT_VIEW_TABS = "PATIENT_VIEW_TABS";
      const existingTabs = StorageService.getFromLocalStorage(PATIENT_VIEW_TABS) || [];
      return existingTabs.map(tab => tab.id);
    } catch (error) {
      console.error("Fallback get order from localStorage failed:", error);
      return [];
    }
  }

  /**
   * Fallback store to localStorage for backwards compatibility - store order
   */
  fallbackStoreOrderToLocalStorage(tabOrder) {
    try {
      const StorageService = require("./storageService").default;
      const PATIENT_VIEW_TABS = "PATIENT_VIEW_TABS";
      // For backwards compatibility, we can't store just IDs to localStorage
      // So we'll read existing tabs, reorder them, and store back
      const existingTabs = StorageService.getFromLocalStorage(PATIENT_VIEW_TABS) || [];
      
      // Reorder existing tabs based on the new order
      const reorderedTabs = [];
      tabOrder.forEach(id => {
        const existingTab = existingTabs.find(tab => tab.id === id);
        if (existingTab) {
          reorderedTabs.push(existingTab);
        }
      });
      
      StorageService.addToLocalStorage(PATIENT_VIEW_TABS, reorderedTabs);
    } catch (error) {
      console.error("Fallback store order to localStorage failed:", error);
    }
  }

  /**
   * Clear tabs for current staff (useful on logout)
   */
  async clearTabsForCurrentStaff() {
    try {
      const staffId = this.getCurrentStaffId();
      if (!staffId) return;

      // Clear cache
      this.tabsCache.delete(staffId);

      // Clear from IndexedDB
      if (this.dbReady && this.db) {
        const transaction = this.db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        store.delete(staffId);
      }
    } catch (error) {
      console.error("Error clearing patient view tabs:", error);
    }
  }

  /**
   * Clear all tabs data (useful for admin cleanup)
   */
  async clearAllTabs() {
    try {
      // Clear all cache
      this.tabsCache.clear();

      // Clear IndexedDB
      if (this.dbReady && this.db) {
        const transaction = this.db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        store.clear();
      }
    } catch (error) {
      console.error("Error clearing all patient view tabs:", error);
    }
  }

  /**
   * Debug helper: List all tabs data in IndexedDB
   */
  async debugListAllTabs() {
    try {
      if (!this.db) await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const results = request.result;
          console.log("All patient view tabs data in IndexedDB:", results);
          resolve(results);
        };

        request.onerror = () => {
          console.error("Failed to get all patient view tabs data");
          reject(request.error);
        };
      });
    } catch (error) {
      console.error("Error listing all patient view tabs:", error);
      return [];
    }
  }
}

const patientViewTabsStorageService = new PatientViewTabsStorageService();

// Add debug helper to window for testing
if (typeof window !== "undefined") {
  window.debugPatientViewTabs = () =>
    patientViewTabsStorageService.debugListAllTabs();
}

export default patientViewTabsStorageService;